/**
 * Atlas PM · 任务设计器 · 单张指标卡（受控编辑，P6.41）
 *
 * 指标：名称 + 占任务权重（0-100）+ 完成要素列表。
 * 要素：名称 / 描述(≤30 字) / 类型 / 完成度(0-100) / 占指标权重(1-100) / 完成质量(0-100) / 是否需要附件。
 *
 * ⭐ 编辑期允许权重合计 ≠100（顶部/底部实时提示），提交任务时强校验。
 */

import { ELEMENT_TYPE } from '@atlaspm/shared';
import type { ElementDraft, MetricDraft } from './types';
import { metricTypeLabel } from './types';

export interface MetricCardProps {
  metric: MetricDraft;
  onChange: (patch: Partial<MetricDraft>) => void;
  onElementChange: (elKey: string, patch: Partial<ElementDraft>) => void;
  onElementRemove: (elKey: string) => void;
  onElementAdd: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
}

const ELEMENT_TYPE_OPTIONS: Array<{ value: ElementDraft['elementType']; label: string }> = [
  { value: ELEMENT_TYPE.TEXT, label: '文本' },
  { value: ELEMENT_TYPE.NUMBER, label: '数值' },
  { value: ELEMENT_TYPE.BOOL, label: '是否' },
  { value: ELEMENT_TYPE.DATE, label: '日期' },
  { value: ELEMENT_TYPE.ENUM, label: '枚举' },
  { value: ELEMENT_TYPE.FILE, label: '文件' },
];

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function clampWeight(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(100, Math.round(n)));
}

export default function MetricCard({
  metric,
  onChange,
  onElementChange,
  onElementRemove,
  onElementAdd,
  onRemove,
  onDuplicate,
}: MetricCardProps) {
  const elementWeightTotal = metric.elements.reduce((s, e) => s + (Number(e.weight) || 0), 0);
  const elementWeightOk = Math.abs(elementWeightTotal - 100) < 1e-9;

  return (
    <article className="td-metric">
      <header className="td-metric__hd">
        <span className="td-metric__drag" aria-hidden="true">⠿</span>
        <input
          className="td-metric__nm"
          value={metric.name}
          aria-label="指标名称"
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <span className="td-metric__ty">{metricTypeLabel(metric.metricType)}</span>
        <span className="td-metric__act">
          <button type="button" className="td-mini" onClick={onDuplicate}>复制</button>
          <button type="button" className="td-mini" onClick={onRemove}>删除</button>
        </span>
      </header>

      <div className="td-metric__cfg">
        <label className="td-fld td-fld--weight">
          <span className="td-fld__label">占任务权重</span>
          <input
            className="td-input td-input--hi"
            value={metric.weight}
            inputMode="numeric"
            aria-label="占任务权重"
            onChange={(e) => onChange({ weight: e.target.value })}
          />
          <span className="td-fld__unit">/100</span>
        </label>
      </div>

      <div className="td-els">
        <div className="td-els__head">
          <span>完成要素（人为设置）</span>
          <span
            className={['td-els__w', elementWeightOk ? 'is-ok' : 'is-warn'].join(' ')}
            data-testid="element-weight-sum"
          >
            要素权重合计 {Math.round(elementWeightTotal)} / 100
          </span>
        </div>

        {metric.elements.map((el) => (
          <div className="td-el td-el--rich" key={el.key} data-el-key={el.key}>
            <div className="td-el__r1">
              <span className="td-el__drag" aria-hidden="true">⠿</span>
              <input
                className="td-el__nm"
                value={el.name}
                aria-label="要素名称"
                placeholder="要素名称"
                onChange={(e) => onElementChange(el.key, { name: e.target.value })}
              />
              <select
                className="td-el__type"
                aria-label="要素类型"
                value={el.elementType}
                onChange={(e) =>
                  onElementChange(el.key, { elementType: e.target.value as ElementDraft['elementType'] })
                }
              >
                {ELEMENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="td-mini td-mini--del"
                aria-label="删除要素"
                onClick={() => onElementRemove(el.key)}
              >
                ✕
              </button>
            </div>

            <div className="td-el__r2">
              <label className="td-el__desc">
                <input
                  className="td-el__desc-in"
                  value={el.description}
                  maxLength={30}
                  aria-label="要素描述"
                  placeholder="描述（≤30 字）"
                  onChange={(e) => onElementChange(el.key, { description: e.target.value.slice(0, 30) })}
                />
                <span className="td-el__counter">{el.description.length}/30</span>
              </label>
            </div>

            <div className="td-el__r3">
              <label className="td-el__num">
                <span>完成度</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={el.completionPct}
                  aria-label="要素完成度"
                  disabled={Boolean(el.id)}
                  title={el.id ? '存量要素的完成度由任务详情页填报，编辑任务时不可改动' : undefined}
                  onChange={(e) => onElementChange(el.key, { completionPct: clampPct(Number(e.target.value)) })}
                />
                <span className="td-el__u">%</span>
              </label>
              <label className="td-el__num">
                <span>占指标权重</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={el.weight}
                  aria-label="占指标权重"
                  onChange={(e) => onElementChange(el.key, { weight: clampWeight(Number(e.target.value)) })}
                />
                <span className="td-el__u">%</span>
              </label>
              <label className="td-el__num">
                <span>完成质量</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={el.qualityPct}
                  aria-label="要素完成质量"
                  disabled={Boolean(el.id)}
                  title={el.id ? '存量要素的完成质量由任务详情页填报，编辑任务时不可改动' : undefined}
                  onChange={(e) => onElementChange(el.key, { qualityPct: clampPct(Number(e.target.value)) })}
                />
                <span className="td-el__u">%</span>
              </label>
              <label className="td-el__req">
                <input
                  type="checkbox"
                  checked={el.requireAttachment}
                  aria-label="需要附件"
                  onChange={(e) => onElementChange(el.key, { requireAttachment: e.target.checked })}
                />
                需要附件
              </label>
            </div>
          </div>
        ))}

        <button type="button" className="td-add-el" onClick={onElementAdd}>
          ＋ 添加要素
        </button>
      </div>
    </article>
  );
}
